/* eslint-disable no-unused-vars */
import React, { useState, useCallback } from 'react';
import {
  SafeAreaView,
  ScrollView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  Image,
  StatusBar,
  ActivityIndicator,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
} from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const { width } = Dimensions.get('window');

const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  companyProfile: `${API_BASE_URL}/api/data/company_profiles`,
  vendorPerformance: `${API_BASE_URL}/api/data/vendor_performance`,
  vendors: `${API_BASE_URL}/api/data/vendors`,  // hypothetical endpoint
};

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
  },
});

api.interceptors.request.use(
  async (config) => {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        config.params = { ...config.params, company_id: parseInt(companyId) };
      }
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const VendorPerformanceDashboardScreen = ({ navigation }) => {
  const [selectedPeriod, setSelectedPeriod] = useState('Last 6 Months');
  const [companyLogo, setCompanyLogo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [performanceData, setPerformanceData] = useState({
    summary: {
      totalVendors: 0,
      activeProjects: 0,
      completedProjects: 0,
      averageRating: 0,
      totalSpent: '£0',
      onTimeCompletion: 0,
    },
    topPerformers: [],
    categoryPerformance: [],
    monthlyTrends: [],
    alerts: [],
  });

  useFocusEffect(
    useCallback(() => {
      loadCompanyLogo();
      fetchPerformanceData();
    }, [])
  );

  const loadCompanyLogo = async () => {
    try {
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        setCompanyLogo(profile.logo || null);
      }
      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        const response = await api.get(API_ENDPOINTS.companyProfile, {
          params: { company_id: companyId },
        });
        if (response.data?.success && response.data.data?.length > 0) {
          const latestProfile = response.data.data[0];
          setCompanyLogo(latestProfile.logo || null);
          await AsyncStorage.setItem('company_profile', JSON.stringify(latestProfile));
        }
      }
    } catch (error) {
      console.warn('Error loading company logo:', error);
    }
  };

  const fetchPerformanceData = async () => {
    setLoading(true);
    try {
      const companyId = await AsyncStorage.getItem('company_id');
      const response = await api.get(API_ENDPOINTS.vendorPerformance, {
        params: { company_id: companyId, period: selectedPeriod },
      });
      if (response.data?.success) {
        // Merge with default structure to ensure all fields exist
        const apiData = response.data.data;
        setPerformanceData({
          summary: {
            totalVendors: apiData.summary?.totalVendors ?? 0,
            activeProjects: apiData.summary?.activeProjects ?? 0,
            completedProjects: apiData.summary?.completedProjects ?? 0,
            averageRating: apiData.summary?.averageRating ?? 0,
            totalSpent: apiData.summary?.totalSpent ?? '£0',
            onTimeCompletion: apiData.summary?.onTimeCompletion ?? 0,
          },
          topPerformers: apiData.topPerformers || [],
          categoryPerformance: apiData.categoryPerformance || [],
          monthlyTrends: apiData.monthlyTrends || [],
          alerts: apiData.alerts || [],
        });
      } else {
        // Fallback to mock data
        setPerformanceData({
          summary: {
            totalVendors: 25,
            activeProjects: 12,
            completedProjects: 48,
            averageRating: 4.3,
            totalSpent: '£1,250,000',
            onTimeCompletion: 87,
          },
          topPerformers: [
            { id: 'sc1', name: 'Elite Construction Services', rating: 4.7, projectsCompleted: 8, onTimeRate: 95, specialty: 'Concrete Work', trend: 'up' },
            { id: 'sc2', name: 'Advanced Plumbing Solutions', rating: 4.6, projectsCompleted: 6, onTimeRate: 92, specialty: 'Plumbing', trend: 'up' },
            { id: 'sc3', name: 'Premier Electrical Contractors', rating: 4.5, projectsCompleted: 7, onTimeRate: 89, specialty: 'Electrical', trend: 'stable' },
          ],
          categoryPerformance: [
            { category: 'Concrete & Foundation', avgRating: 4.5, vendors: 5, projects: 15 },
            { category: 'Electrical', avgRating: 4.3, vendors: 4, projects: 12 },
            { category: 'Plumbing', avgRating: 4.4, vendors: 3, projects: 8 },
            { category: 'Roofing', avgRating: 4.2, vendors: 3, projects: 6 },
            { category: 'Interior Finishing', avgRating: 4.1, vendors: 4, projects: 10 },
          ],
          monthlyTrends: [
            { month: 'Jul', projects: 8, rating: 4.2, cost: 180000 },
            { month: 'Aug', projects: 12, rating: 4.3, cost: 220000 },
            { month: 'Sep', projects: 10, rating: 4.4, cost: 195000 },
            { month: 'Oct', projects: 15, rating: 4.3, cost: 280000 },
            { month: 'Nov', projects: 11, rating: 4.5, cost: 210000 },
            { month: 'Dec', projects: 9, rating: 4.4, cost: 165000 },
          ],
          alerts: [
            { id: 'a1', type: 'warning', title: 'Low Performance Alert', message: 'Precision Drywall & Finishing has dropped below 4.0 rating', date: '2 days ago' },
            { id: 'a2', type: 'info', title: 'New High Performer', message: 'Elite Construction Services achieved 95% on-time completion rate', date: '1 week ago' },
            { id: 'a3', type: 'success', title: 'Cost Savings', message: 'Monthly vendor costs reduced by 12%', date: '2 weeks ago' },
          ],
        });
      }
    } catch (error) {
      console.error('Error fetching performance data:', error);
      // Keep default or fallback mock
      setPerformanceData(prev => prev); // no change, but we could set fallback here too
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    if (navigation.canGoBack()) navigation.goBack();
  };

  const renderSummaryCard = (title, value, subtitle, icon, color) => (
    <View style={[styles.summaryCard, { borderLeftColor: color }]}>
      <View style={styles.summaryContent}>
        <Text style={styles.summaryTitle}>{title}</Text>
        <Text style={styles.summaryValue}>{value}</Text>
        {subtitle && <Text style={styles.summarySubtitle}>{subtitle}</Text>}
      </View>
      <View style={[styles.summaryIcon, { backgroundColor: color }]}>
        <Ionicons name={icon} size={24} color="#fff" />
      </View>
    </View>
  );

  const renderPerformanceChart = () => (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>Performance Trends</Text>
        <TouchableOpacity style={styles.periodSelector}>
          <Text style={styles.periodText}>{selectedPeriod}</Text>
          <Ionicons name="chevron-down" size={16} color="#64748B" />
        </TouchableOpacity>
      </View>
      <View style={styles.chartContainer}>
        <View style={styles.chartHeader}>
          <Text style={styles.chartTitle}>Monthly Project Completion</Text>
        </View>
        <View style={styles.chart}>
          <View style={styles.yAxis}>
            {[20, 15, 10, 5, 0].map(v => <Text key={v} style={styles.yAxisLabel}>{v}</Text>)}
          </View>
          <View style={styles.chartArea}>
            {performanceData.monthlyTrends.map(data => (
              <View key={data.month} style={styles.chartColumn}>
                <View style={[styles.chartBar, { height: `${(data.projects / 20) * 100}%`, backgroundColor: '#0e4e96' }]} />
                <Text style={styles.xAxisLabel}>{data.month}</Text>
              </View>
            ))}
          </View>
        </View>
      </View>
    </View>
  );

  const renderTopPerformers = () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Top Performers</Text>
      {performanceData.topPerformers.map((performer, index) => (
        <TouchableOpacity
          key={performer.id}
          style={styles.performerCard}
          onPress={() => navigation.navigate('SubcontractorDetail', { subcontractorId: performer.id })}
        >
          <View style={styles.performerRank}><Text style={styles.rankNumber}>{index + 1}</Text></View>
          <View style={styles.performerInfo}>
            <Text style={styles.performerName}>{performer.name}</Text>
            <Text style={styles.performerSpecialty}>{performer.specialty}</Text>
            <View style={styles.performerMetrics}>
              <View style={styles.metricItem}><Ionicons name="star" size={14} color="#fdb913" /><Text style={styles.metricText}>{performer.rating}</Text></View>
              <View style={styles.metricItem}><MaterialCommunityIcons name="clipboard-check" size={14} color="#0e4e96" /><Text style={styles.metricText}>{performer.projectsCompleted} projects</Text></View>
              <View style={styles.metricItem}><Ionicons name="time" size={14} color="#10B981" /><Text style={styles.metricText}>{performer.onTimeRate}% on-time</Text></View>
            </View>
          </View>
          <View style={styles.performerTrend}>
            <Ionicons name={performer.trend === 'up' ? 'trending-up' : 'remove'} size={20} color={performer.trend === 'up' ? '#10B981' : '#64748B'} />
          </View>
        </TouchableOpacity>
      ))}
    </View>
  );

  const renderCategoryPerformance = () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Performance by Category</Text>
      {performanceData.categoryPerformance.map((cat, idx) => (
        <View key={idx} style={styles.categoryCard}>
          <View style={styles.categoryHeader}>
            <Text style={styles.categoryName}>{cat.category}</Text>
            <View style={styles.categoryRating}><Ionicons name="star" size={14} color="#fdb913" /><Text style={styles.categoryRatingText}>{Number(cat.avgRating).toFixed(1)}</Text></View>
          </View>
          <View style={styles.categoryStats}>
            <View style={styles.categoryStat}><Text style={styles.categoryStatValue}>{cat.vendors}</Text><Text style={styles.categoryStatLabel}>Vendors</Text></View>
            <View style={styles.categoryStat}><Text style={styles.categoryStatValue}>{cat.projects}</Text><Text style={styles.categoryStatLabel}>Projects</Text></View>
            <View style={styles.categoryProgressContainer}>
              <View style={styles.categoryProgress}>
                <View style={[styles.categoryProgressBar, { width: `${(Number(cat.avgRating) / 5) * 100}%` }]} />
              </View>
            </View>
          </View>
        </View>
      ))}
    </View>
  );

  const renderAlerts = () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Recent Alerts</Text>
      {performanceData.alerts.map(alert => (
        <View key={alert.id} style={styles.alertCard}>
          <View style={styles.alertIcon}>
            <Ionicons name={alert.type === 'warning' ? 'warning' : alert.type === 'success' ? 'checkmark-circle' : 'information-circle'} size={20} color={alert.type === 'warning' ? '#EF4444' : alert.type === 'success' ? '#10B981' : '#0e4e96'} />
          </View>
          <View style={styles.alertContent}>
            <Text style={styles.alertTitle}>{alert.title}</Text>
            <Text style={styles.alertMessage}>{alert.message}</Text>
            <Text style={styles.alertDate}>{alert.date}</Text>
          </View>
        </View>
      ))}
    </View>
  );

  const renderHeader = () => (
    <View style={styles.header}>
      <TouchableOpacity style={styles.backButton} onPress={handleGoBack}>
        <Ionicons name="chevron-back" size={24} color="#fff" />
      </TouchableOpacity>
      <View style={styles.headerCenter}>
        {companyLogo ? <Image source={{ uri: companyLogo || undefined }} style={styles.headerLogo} /> : <MaterialCommunityIcons name="office-building" size={30} color="#fff" />}
        <Text style={styles.headerTitle}>Performance Dashboard</Text>
      </View>
      <TouchableOpacity style={styles.headerButton}><Ionicons name="download-outline" size={24} color="#fff" /></TouchableOpacity>
    </View>
  );

  if (loading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
        {renderHeader()}
        <View style={styles.loadingContainer}><ActivityIndicator size={36} color="#0e4e96" /><Text style={styles.loadingText}>Loading performance data...</Text></View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
      {renderHeader()}
      <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
        <View style={styles.summaryContainer}>
          <View style={styles.summaryRow}>
            {renderSummaryCard('Total Vendors', performanceData.summary.totalVendors.toString(), 'Active contractors', 'people', '#0e4e96')}
            {renderSummaryCard('Active Projects', performanceData.summary.activeProjects.toString(), 'In progress', 'construct', '#fdb913')}
          </View>
          <View style={styles.summaryRow}>
            {renderSummaryCard('Avg Rating', performanceData.summary.averageRating.toString(), 'Overall', 'star', '#10B981')}
            {renderSummaryCard('On-Time Rate', `${performanceData.summary.onTimeCompletion}%`, 'Project completion', 'time', '#EF4444')}
          </View>
          <View style={styles.summaryFullWidth}>
            {renderSummaryCard('Total Spent', performanceData.summary.totalSpent, `${performanceData.summary.completedProjects} completed projects`, 'cash', '#64748B')}
          </View>
        </View>
        {renderPerformanceChart()}
        {renderTopPerformers()}
        {renderCategoryPerformance()}
        {renderAlerts()}
        <View style={styles.bottomPadding} />
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  // ... (styles unchanged, same as previous version)
  safeArea: { flex: 1, backgroundColor: '#4A90E2' },
  header: {
    backgroundColor: '#4A90E2',
    paddingVertical: 15,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: { padding: 5 },
  headerCenter: { flexDirection: 'row', alignItems: 'center' },
  headerLogo: { width: 30, height: 30, borderRadius: 15, resizeMode: 'cover' },
  headerTitle: { color: '#fff', fontSize: 20, fontWeight: 'bold', marginLeft: 10 },
  headerButton: { padding: 5 },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#f8fafc' },
  loadingText: { marginTop: 10, fontSize: 16, color: '#64748B' },
  container: { flex: 1, backgroundColor: '#f8fafc' },
  summaryContainer: { padding: 15 },
  summaryRow: { flexDirection: 'row', marginBottom: 10, gap: 10 },
  summaryFullWidth: { marginBottom: 10 },
  summaryCard: {
    flex: 1,
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 15,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 10,
    elevation: 2,
    borderLeftWidth: 4,
  },
  summaryContent: { flex: 1 },
  summaryTitle: { fontSize: 12, color: '#64748B', marginBottom: 5 },
  summaryValue: { fontSize: 20, fontWeight: 'bold', color: '#0f172a', marginBottom: 2 },
  summarySubtitle: { fontSize: 10, color: '#94A3B8' },
  summaryIcon: { width: 40, height: 40, borderRadius: 20, justifyContent: 'center', alignItems: 'center' },
  section: {
    backgroundColor: '#fff',
    marginHorizontal: 15,
    marginBottom: 15,
    borderRadius: 12,
    padding: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 10,
    elevation: 2,
  },
  sectionHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 },
  sectionTitle: { fontSize: 18, fontWeight: '600', color: '#0f172a' },
  periodSelector: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#f1f5f9', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 4, borderWidth: 1, borderColor: '#e2e8f0' },
  periodText: { fontSize: 12, color: '#64748B', marginRight: 5 },
  chartContainer: { marginTop: 10 },
  chartHeader: { marginBottom: 15 },
  chartTitle: { fontSize: 14, fontWeight: '600', color: '#0f172a' },
  chart: { flexDirection: 'row', height: 150 },
  yAxis: { width: 30, justifyContent: 'space-between', alignItems: 'flex-end', paddingRight: 10 },
  yAxisLabel: { fontSize: 10, color: '#64748B' },
  chartArea: { flex: 1, flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', paddingHorizontal: 5 },
  chartColumn: { flex: 1, alignItems: 'center', marginHorizontal: 2 },
  chartBar: { width: '80%', borderRadius: 2, marginBottom: 5 },
  xAxisLabel: { fontSize: 10, color: '#64748B' },
  performerCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    padding: 12,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  performerRank: { width: 30, height: 30, borderRadius: 15, backgroundColor: '#0e4e96', justifyContent: 'center', alignItems: 'center', marginRight: 12 },
  rankNumber: { color: '#fff', fontSize: 14, fontWeight: 'bold' },
  performerInfo: { flex: 1 },
  performerName: { fontSize: 16, fontWeight: '600', color: '#0f172a', marginBottom: 2 },
  performerSpecialty: { fontSize: 12, color: '#64748B', marginBottom: 8 },
  performerMetrics: { flexDirection: 'row', gap: 15 },
  metricItem: { flexDirection: 'row', alignItems: 'center' },
  metricText: { fontSize: 11, color: '#64748B', marginLeft: 3 },
  performerTrend: { marginLeft: 10 },
  categoryCard: {
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    padding: 12,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  categoryHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  categoryName: { fontSize: 14, fontWeight: '600', color: '#0f172a' },
  categoryRating: { flexDirection: 'row', alignItems: 'center' },
  categoryRatingText: { fontSize: 14, fontWeight: 'bold', color: '#0f172a', marginLeft: 3 },
  categoryStats: { flexDirection: 'row', alignItems: 'center' },
  categoryStat: { alignItems: 'center', marginRight: 20 },
  categoryStatValue: { fontSize: 16, fontWeight: 'bold', color: '#0e4e96' },
  categoryStatLabel: { fontSize: 10, color: '#64748B' },
  categoryProgressContainer: { flex: 1, marginLeft: 15 },
  categoryProgress: { height: 6, backgroundColor: '#e2e8f0', borderRadius: 3 },
  categoryProgressBar: { height: '100%', backgroundColor: '#0e4e96', borderRadius: 3 },
  alertCard: {
    flexDirection: 'row',
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    padding: 12,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  alertIcon: { marginRight: 12, marginTop: 2 },
  alertContent: { flex: 1 },
  alertTitle: { fontSize: 14, fontWeight: '600', color: '#0f172a', marginBottom: 3 },
  alertMessage: { fontSize: 12, color: '#64748B', lineHeight: 16, marginBottom: 5 },
  alertDate: { fontSize: 10, color: '#94A3B8' },
  bottomPadding: { height: 20 },
});

export default VendorPerformanceDashboardScreen;